import { formatDate, label } from "@/admin/model";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useAdminStore } from "@/stores/adminStore";
import { filterCompanies, type CompanyFilters } from "@/admin/model";
import { CompanyLink, SelectField, Status } from "@/admin/components";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "@/components/DataState";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/admin/companies/")({ component: AdminCompanies });
function AdminCompanies() {
  const state = useAdminStore();
  const [filters, setFilters] = useState<CompanyFilters>({
    search: "",
    type: "",
    access: "",
    managed: "",
    onboarding: "",
  });
  const update = (key: keyof CompanyFilters) => (value: string) =>
    setFilters((previous) => ({ ...previous, [key]: value }));
  const companies = filterCompanies(state.companies, state.profiles, filters);
  return (
    <>
      <PageHeader
        title="Companies"
        subtitle="Inspect company readiness, management and commercial access."
      />
      <div className="surface-elevated grid items-end gap-3 p-4 md:grid-cols-3 xl:grid-cols-5">
        <label className="grid gap-1.5 text-xs font-semibold text-muted-foreground">
          Search
          <Input
            aria-label="Search companies"
            placeholder="Name, domain or ID"
            value={filters.search}
            onChange={(e) => update("search")(e.target.value)}
          />
        </label>
        <SelectField
          label="Commercial type"
          value={filters.type}
          onChange={update("type")}
          options={[
            ["", "All types"],
            ["standard", "Standard"],
            ["design_partner", "Design partner"],
            ["missing", "Not configured"],
          ]}
        />
        <SelectField
          label="Access status"
          value={filters.access}
          onChange={update("access")}
          options={[
            ["", "All access states"],
            ["active", "Active"],
            ["inactive", "Inactive"],
            ["suspended", "Suspended"],
            ["missing", "Not configured"],
          ]}
        />
        <SelectField
          label="Management"
          value={filters.managed}
          onChange={update("managed")}
          options={[
            ["", "All companies"],
            ["managed", "Managed"],
            ["unmanaged", "Unmanaged"],
          ]}
        />
        <SelectField
          label="Onboarding"
          value={filters.onboarding}
          onChange={update("onboarding")}
          options={[
            ["", "All onboarding states"],
            ...Array.from(new Set(state.companies.map((c) => c.onboardingStatus)))
              .sort()
              .map((s): [string, string] => [s || "missing", s || "Not recorded"]),
          ]}
        />
      </div>
      {state.profileError && (
        <p role="alert" className="text-sm text-warning">
          Company names and languages could not load. Domain search remains available.
        </p>
      )}
      {state.status === "error" ? (
        <ErrorState error={state.error} />
      ) : state.status === "idle" || state.status === "loading" ? (
        <LoadingState label="Loading companies…" />
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {companies.length} of {state.companies.length} companies
          </p>
          {!companies.length ? (
            <EmptyState message="No companies match these filters." />
          ) : (
            <div className="surface-elevated overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border bg-secondary/30 text-xs text-muted-foreground">
                  <tr>
                    {[
                      "Company / Domain",
                      "Customer ID",
                      "Onboarding",
                      "Qotta fit",
                      "Commercial type",
                      "Access",
                      "Management",
                      "Language",
                      "Created",
                    ].map((s) => (
                      <th key={s} className="whitespace-nowrap px-4 py-3 font-semibold">
                        {s}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {companies.map((company) => (
                    <tr key={company.id} className="hover:bg-primary-soft/20">
                      <td className="min-w-48 px-4 py-4">
                        <CompanyLink company={company} profile={state.profiles[company.id]} />
                        <div className="mt-1 text-xs text-muted-foreground">
                          {company.domain || company.website || "Domain not recorded"}
                        </div>
                        {!state.profiles[company.id]?.name && (
                          <div className="text-xs text-muted-foreground">Name unavailable</div>
                        )}
                      </td>
                      <td className="max-w-48 break-all px-4 py-4 font-mono text-xs">
                        {company.id}
                      </td>
                      <td className="px-4 py-4">
                        <Status value={company.onboardingStatus} />
                      </td>
                      <td className="px-4 py-4">
                        {company.qottaFit === null ? "Unknown" : company.qottaFit ? "Yes" : "No"}
                      </td>
                      <td className="px-4 py-4">
                        <Status value={company.commercial?.type} />
                      </td>
                      <td className="px-4 py-4">
                        <Status value={company.commercial?.accessStatus} />
                      </td>
                      <td className="px-4 py-4">
                        <Status value={company.currentManagerUid ? "managed" : "unmanaged"} />
                      </td>
                      <td className="px-4 py-4">
                        {state.profiles[company.id]?.language || "Not recorded"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-xs text-muted-foreground">
                        {formatDate(company.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}
